import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Alert,
  Collapse,
  Autocomplete,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  HelpOutline as HelpOutlineIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  ThumbUp as ThumbUpIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getUserDetails } from 'modules/platform-components/utils/shared-data.service';
import { usePermission } from 'modules/platform-components/utils/PermissionGate';
import { filterBySubjectScope } from 'modules/platform-components/utils/subject-scope';
import {
  apiPaths,
  getInstutueApp,
  putInstituteAppData,
} from 'modules/platform-components/api-integration-util';

const getHeaders = () => {
  const user = getUserDetails();
  return { tenantId: user.tenantId, userEmail: user.email };
};

// Parse tags from JSON string or return as-is if already an array
const parseTags = (tags) => {
  if (!tags) return [];
  if (Array.isArray(tags)) return tags;
  try { return JSON.parse(tags); } catch (e) { return []; }
};

// ─── Conflict type config ──────────────────────────────────────────
const conflictTypeConfig = {
  ALL_AGREE: { label: 'All Agree', color: '#22c55e', icon: <CheckCircleIcon fontSize="small" /> },
  SUBJECT_CONFLICT: { label: 'Subject Conflict', color: '#dc3545', icon: <ErrorIcon fontSize="small" /> },
  TOPIC_CONFLICT: { label: 'Topic Conflict', color: '#dc3545', icon: <WarningIcon fontSize="small" /> },
  SUBTOPIC_ONLY: { label: 'Subtopic Only', color: '#f59e0b', icon: <WarningIcon fontSize="small" /> },
  TAGS_ONLY_CONFLICT: { label: 'Tags Only', color: '#f59e0b', icon: <InfoIcon fontSize="small" /> },
  DB_VS_AI: { label: 'DB vs AI', color: '#f59e0b', icon: <WarningIcon fontSize="small" /> },
  AI_DISAGREE: { label: 'AI Disagree', color: '#dc3545', icon: <WarningIcon fontSize="small" /> },
  FULL_CONFLICT: { label: 'Full Conflict', color: '#dc3545', icon: <ErrorIcon fontSize="small" /> },
  DB_MISSING: { label: 'No DB Mapping', color: '#8492a6', icon: <HelpOutlineIcon fontSize="small" /> },
};

const statusConfig = {
  PROCESSING: { label: 'Processing...', color: '#8b5cf6' },
  PENDING:   { label: 'Pending',   color: '#f59e0b' },
  REVIEWED:  { label: 'Reviewed',  color: '#3366FF' },
  RESOLVED:  { label: 'Resolved',  color: '#22c55e' },
  DISMISSED: { label: 'Dismissed', color: '#8492a6' },
};

const providerColor = {
  OPENAI: '#10a37f',
  ANTHROPIC: '#d97706',
  GOOGLE_GEMINI: '#4285f4',
  UNKNOWN: '#666',
};

// ─── Classification Comparison Table ───────────────────────────────
function ClassificationComparisonTable({ conflict, readOnly }) {
  if (!conflict) return null;

  const matchColor = (val, dbVal) => {
    if (!val || !dbVal) return '#666';
    return val === dbVal ? '#22c55e' : '#dc3545';
  };

  const ConfidenceBadge = ({ confidence }) => {
    if (confidence == null) return null;
    return (
      <Chip
        label={`${(confidence * 100).toFixed(0)}%`}
        size="small"
        variant="outlined"
        sx={{ ml: 0.5, fontSize: 10, height: 18 }}
      />
    );
  };

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
            <TableCell><strong>Source</strong></TableCell>
            <TableCell><strong>Subject</strong></TableCell>
            <TableCell><strong>Topic</strong></TableCell>
            <TableCell><strong>Subtopic</strong></TableCell>
            <TableCell><strong>Tags</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* DB Row */}
          <TableRow>
            <TableCell><Chip label="Database" size="small" /></TableCell>
            <TableCell><strong>{conflict.dbSubjectName || '—'}</strong></TableCell>
            <TableCell><strong>{conflict.dbTopicName || '—'}</strong></TableCell>
            <TableCell>{conflict.db_subtopic_name || conflict.dbSubtopicName || '--'}</TableCell>
            <TableCell>{/* Tags not tracked in DB mapping */}{'--'}</TableCell>
          </TableRow>
          {/* Primary Row */}
          {conflict.primarySubjectName && (
            <TableRow>
              <TableCell>
                <Chip
                  label={`Primary: ${conflict.primaryProvider || '?'}`}
                  size="small"
                  sx={{ backgroundColor: providerColor[conflict.primaryProvider] || '#666', color: '#fff' }}
                />
              </TableCell>
              <TableCell>
                <strong style={{ color: matchColor(conflict.primarySubjectName, conflict.dbSubjectName) }}>
                  {conflict.primarySubjectName}
                </strong>
                <ConfidenceBadge confidence={conflict.primarySubjectConfidence} />
              </TableCell>
              <TableCell>
                <strong style={{ color: matchColor(conflict.primaryTopicName, conflict.dbTopicName) }}>
                  {conflict.primaryTopicName || '—'}
                </strong>
                <ConfidenceBadge confidence={conflict.primaryTopicConfidence} />
              </TableCell>
              <TableCell>
                <span style={{ color: '#666' }}>{conflict.primarySubtopicName || '—'}</span>
                <ConfidenceBadge confidence={conflict.primarySubtopicConfidence} />
              </TableCell>
              <TableCell>
                {parseTags(conflict.primaryTags).length > 0
                  ? parseTags(conflict.primaryTags).map((tag, i) => (
                      <Chip key={i} label={tag} size="small" sx={{ mr: 0.5, mb: 0.5, fontSize: 10, height: 20 }} />
                    ))
                  : '—'}
              </TableCell>
            </TableRow>
          )}
          {/* Secondary Row */}
          {conflict.secondarySubjectName && (
            <TableRow>
              <TableCell>
                <Chip
                  label={`Secondary: ${conflict.secondaryProvider || '?'}`}
                  size="small"
                  sx={{ backgroundColor: providerColor[conflict.secondaryProvider] || '#666', color: '#fff' }}
                />
              </TableCell>
              <TableCell>
                <strong style={{ color: matchColor(conflict.secondarySubjectName, conflict.dbSubjectName) }}>
                  {conflict.secondarySubjectName}
                </strong>
                <ConfidenceBadge confidence={conflict.secondarySubjectConfidence} />
              </TableCell>
              <TableCell>
                <strong style={{ color: matchColor(conflict.secondaryTopicName, conflict.dbTopicName) }}>
                  {conflict.secondaryTopicName || '—'}
                </strong>
                <ConfidenceBadge confidence={conflict.secondaryTopicConfidence} />
              </TableCell>
              <TableCell>
                <span style={{ color: '#666' }}>{conflict.secondarySubtopicName || '—'}</span>
                <ConfidenceBadge confidence={conflict.secondarySubtopicConfidence} />
              </TableCell>
              <TableCell>
                {parseTags(conflict.secondaryTags).length > 0
                  ? parseTags(conflict.secondaryTags).map((tag, i) => (
                      <Chip key={i} label={tag} size="small" sx={{ mr: 0.5, mb: 0.5, fontSize: 10, height: 20 }} />
                    ))
                  : '—'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ─── Reasoning Panel (expandable) ──────────────────────────────────
function ReasoningPanel({ label, provider, model, confidence, reasoning }) {
  const [expanded, setExpanded] = useState(false);
  if (!reasoning) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 1 }}>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{label}</Typography>
          {provider && (
            <Chip
              label={`${provider} / ${model || '?'}`}
              size="small"
              sx={{ fontSize: 10, height: 18, backgroundColor: providerColor[provider] || '#666', color: '#fff' }}
            />
          )}
          {confidence != null && (
            <Chip
              label={`${(confidence * 100).toFixed(0)}%`}
              size="small"
              variant="outlined"
              sx={{ fontSize: 10, height: 18 }}
            />
          )}
        </Box>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Typography variant="body2" sx={{ mt: 1, fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
          {reasoning}
        </Typography>
      </Collapse>
    </Paper>
  );
}

// ─── Stats Dashboard ────────────────────────────────────────────────
function StatsDashboard({ stats }) {
  if (!stats) return null;

  const cards = [
    { label: 'Total Verified', value: stats.total_verified, color: '#3366FF' },
    { label: 'All Agree', value: stats.all_agree, color: '#22c55e' },
    { label: 'Subject Conflicts', value: stats.subject_conflicts, color: '#dc3545' },
    { label: 'Topic Conflicts', value: stats.topic_conflicts, color: '#dc3545' },
    { label: 'DB vs AI', value: stats.db_vs_ai, color: '#f59e0b' },
    { label: 'Pending Review', value: stats.pending_review, color: '#dc3545' },
    { label: 'Resolved', value: stats.resolved, color: '#22c55e' },
    { label: 'Classifications Corrected', value: stats.classifications_corrected, color: '#7c3aed' },
    { label: 'Dismissed', value: stats.dismissed, color: '#8492a6' },
  ];

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
      {cards.map((card) => (
        <Paper key={card.label} sx={{ p: 2, minWidth: 130, textAlign: 'center', borderTop: `3px solid ${card.color}` }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: card.color }}>{card.value || 0}</Typography>
          <Typography variant="caption" color="textSecondary">{card.label}</Typography>
        </Paper>
      ))}
    </Box>
  );
}

// ─── Detail Dialog (read-only, for RESOLVED/DISMISSED) ─────────────
function DetailDialog({ open, conflict, onClose, onCorrectClassification, onRefresh }) {
  const canCorrectClassification = usePermission('QA Actions', 'Correct Classification');
  const [showCorrection, setShowCorrection] = useState(false);
  const [correcting, setCorrecting] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);

  // Correction form state
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subtopics, setSubtopics] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [selectedSubtopic, setSelectedSubtopic] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [correctionNotes, setCorrectionNotes] = useState('');

  useEffect(() => {
    if (conflict && open) {
      setShowCorrection(false);
      setCorrectionNotes('');
      setSelectedSubject(conflict.resolvedSubjectId || conflict.dbSubjectId || '');
      setSelectedTopic(conflict.resolvedTopicId || conflict.dbTopicId || '');
      setSelectedSubtopic(conflict.resolvedSubtopicId || '');
      setSelectedTags(parseTags(conflict.resolvedTags));
      fetchAuditLog();
      fetchTaxonomy();
    }
  }, [conflict, open]);

  const fetchAuditLog = async () => {
    if (!conflict) return;
    setLoadingAudit(true);
    try {
      const data = await getInstutueApp(
        apiPaths.classificationConflictsAudit + '/' + conflict.questionId,
        null,
        getHeaders()
      );
      if (data?.success) {
        setAuditLog(data.auditLog || []);
      }
    } catch (e) {
      console.error('Failed to fetch audit log', e);
    } finally {
      setLoadingAudit(false);
    }
  };

  const fetchTaxonomy = async () => {
    try {
      const data = await getInstutueApp(
        apiPaths.classificationConflicts + '/taxonomy',
        null,
        getHeaders()
      );
      if (data?.success && data.subjects) {
        setSubjects(data.subjects);
      }
    } catch (e) {
      console.error('Failed to fetch taxonomy', e);
    }
  };

  const fetchTopicsForSubject = async (subjectId) => {
    if (!subjectId) { setTopics([]); setSubtopics([]); return; }
    try {
      const data = await getInstutueApp(
        apiPaths.classificationConflicts + '/taxonomy/topics',
        { subjectId },
        getHeaders()
      );
      if (data?.success) {
        setTopics(data.topics || []);
      }
    } catch (e) {
      console.error('Failed to fetch topics', e);
      setTopics([]);
    }
  };

  const fetchSubtopicsForTopic = async (topicId) => {
    if (!topicId) { setSubtopics([]); return; }
    try {
      const data = await getInstutueApp(
        apiPaths.classificationConflicts + '/taxonomy/subtopics',
        { topicId },
        getHeaders()
      );
      if (data?.success) {
        setSubtopics(data.subtopics || []);
      }
    } catch (e) {
      console.error('Failed to fetch subtopics', e);
      setSubtopics([]);
    }
  };

  const handleSubjectChange = (subjectId) => {
    setSelectedSubject(subjectId);
    setSelectedTopic('');
    setSelectedSubtopic('');
    fetchTopicsForSubject(subjectId);
  };

  const handleTopicChange = (topicId) => {
    setSelectedTopic(topicId);
    setSelectedSubtopic('');
    fetchSubtopicsForTopic(topicId);
  };

  const handleCorrect = async () => {
    if (!selectedSubject) {
      toast.error('Please select a subject');
      return;
    }
    setCorrecting(true);
    try {
      await onCorrectClassification(conflict.id, {
        subjectId: selectedSubject,
        topicId: selectedTopic,
        subtopicId: selectedSubtopic,
        tags: selectedTags,
        notes: correctionNotes,
      });
      toast.success('Classification corrected');
      setShowCorrection(false);
      if (onRefresh) onRefresh();
      onClose();
    } catch (e) {
      toast.error('Failed to correct classification: ' + (e.message || e));
    } finally {
      setCorrecting(false);
    }
  };

  if (!conflict) return null;

  const isResolved = conflict.status === 'RESOLVED';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Classification Details — Question #{conflict.questionId}</span>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {conflictTypeConfig[conflict.conflictType] && (
            <Chip
              icon={conflictTypeConfig[conflict.conflictType].icon}
              label={conflictTypeConfig[conflict.conflictType].label}
              size="small"
              sx={{ backgroundColor: conflictTypeConfig[conflict.conflictType].color, color: '#fff' }}
            />
          )}
          {statusConfig[conflict.status] && (
            <Chip
              label={statusConfig[conflict.status].label}
              size="small"
              variant="outlined"
              sx={{ borderColor: statusConfig[conflict.status].color, color: statusConfig[conflict.status].color }}
            />
          )}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Classification comparison */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Classification Comparison</Typography>
        <ClassificationComparisonTable conflict={conflict} readOnly />

        {/* Reasoning panels */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>AI Reasoning</Typography>
        <ReasoningPanel
          label="Primary"
          provider={conflict.primaryProvider}
          model={conflict.primaryModel}
          confidence={conflict.primaryConfidence}
          reasoning={conflict.primaryReasoning}
        />
        <ReasoningPanel
          label="Secondary"
          provider={conflict.secondaryProvider}
          model={conflict.secondaryModel}
          confidence={conflict.secondaryConfidence}
          reasoning={conflict.secondaryReasoning}
        />

        {/* Resolution info */}
        {isResolved && conflict.resolvedBy && (
          <Alert
            severity="info"
            sx={{ mt: 2 }}
            action={
              !showCorrection && canCorrectClassification && (
                <Button size="small" color="inherit" onClick={() => setShowCorrection(true)}>
                  Correct Classification
                </Button>
              )
            }
          >
            Resolved by <strong>{conflict.resolvedBy}</strong>
            {conflict.resolvedSubjectName && <> — Subject: <strong>{conflict.resolvedSubjectName}</strong></>}
            {conflict.resolvedTopicName && <>, Topic: <strong>{conflict.resolvedTopicName}</strong></>}
            {conflict.resolvedAt && ` on ${new Date(conflict.resolvedAt).toLocaleString()}`}
          </Alert>
        )}

        {conflict.status === 'DISMISSED' && conflict.resolvedBy && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Dismissed by <strong>{conflict.resolvedBy}</strong>
            {conflict.resolvedAt && ` on ${new Date(conflict.resolvedAt).toLocaleString()}`}
            {conflict.notes && <> — {conflict.notes}</>}
          </Alert>
        )}

        {/* Post-resolution correction form */}
        {isResolved && showCorrection && (
          <Paper variant="outlined" sx={{ mt: 2, p: 2, borderColor: '#f59e0b', backgroundColor: '#fffbeb' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: '#d97706' }}>
              Correct Classification
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Subject</InputLabel>
                <Select value={selectedSubject} label="Subject" onChange={(e) => handleSubjectChange(e.target.value)}>
                  <MenuItem value="">-- Select --</MenuItem>
                  {subjects.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Topic</InputLabel>
                <Select value={selectedTopic} label="Topic" onChange={(e) => handleTopicChange(e.target.value)} disabled={!selectedSubject}>
                  <MenuItem value="">-- Select --</MenuItem>
                  {topics.map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Subtopic</InputLabel>
                <Select value={selectedSubtopic} label="Subtopic" onChange={(e) => setSelectedSubtopic(e.target.value)} disabled={!selectedTopic}>
                  <MenuItem value="">-- Select --</MenuItem>
                  {subtopics.map((st) => (
                    <MenuItem key={st.id} value={st.id}>{st.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={[]}
              value={selectedTags}
              onChange={(_, newVal) => setSelectedTags(newVal)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip label={option} size="small" {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => <TextField {...params} label="Tags" placeholder="Add tags..." />}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Reason for correction (optional)"
              size="small"
              fullWidth
              sx={{ mb: 1 }}
              value={correctionNotes}
              onChange={(e) => setCorrectionNotes(e.target.value)}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="contained" color="warning" onClick={handleCorrect} disabled={correcting}>
                {correcting ? <CircularProgress size={16} /> : 'Update Classification'}
              </Button>
              <Button size="small" variant="outlined" onClick={() => setShowCorrection(false)} disabled={correcting}>
                Cancel
              </Button>
            </Box>
          </Paper>
        )}

        {/* Audit log */}
        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>Audit Log</Typography>
        {loadingAudit ? (
          <CircularProgress size={20} />
        ) : auditLog.length === 0 ? (
          <Typography variant="body2" color="textSecondary">No audit entries found.</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Action</TableCell>
                  <TableCell>By</TableCell>
                  <TableCell>Details</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditLog.map((entry, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Chip label={entry.action} size="small" sx={{ fontSize: 10, height: 20 }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{entry.performedBy || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{entry.details || '—'}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {entry.createdAt ? new Date(entry.createdAt).toLocaleString() : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Resolve Dialog (for PENDING conflicts) ────────────────────────
function ResolveDialog({ open, conflict, onClose, onResolve }) {
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subtopics, setSubtopics] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [selectedSubtopic, setSelectedSubtopic] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [resolutionSource, setResolutionSource] = useState(null);
  const [primaryExpanded, setPrimaryExpanded] = useState(false);
  const [secondaryExpanded, setSecondaryExpanded] = useState(false);

  useEffect(() => {
    if (conflict && open) {
      setSelectedSubject(conflict.dbSubjectId || '');
      setSelectedTopic(conflict.dbTopicId || '');
      setSelectedSubtopic('');
      setSelectedTags([]);
      setNotes('');
      setResolutionSource(null);
      fetchTaxonomy();
      if (conflict.dbSubjectId) {
        fetchTopicsForSubject(conflict.dbSubjectId);
      }
      if (conflict.dbTopicId) {
        fetchSubtopicsForTopic(conflict.dbTopicId);
      }
    }
  }, [conflict, open]);

  const fetchTaxonomy = async () => {
    try {
      const data = await getInstutueApp(
        apiPaths.classificationConflicts + '/taxonomy',
        null,
        getHeaders()
      );
      if (data?.success && data.subjects) {
        setSubjects(data.subjects);
      }
    } catch (e) {
      console.error('Failed to fetch taxonomy', e);
    }
  };

  const fetchTopicsForSubject = async (subjectId) => {
    if (!subjectId) { setTopics([]); setSubtopics([]); return; }
    try {
      const data = await getInstutueApp(
        apiPaths.classificationConflicts + '/taxonomy/topics',
        { subjectId },
        getHeaders()
      );
      if (data?.success) {
        setTopics(data.topics || []);
      }
    } catch (e) {
      console.error('Failed to fetch topics', e);
      setTopics([]);
    }
  };

  const fetchSubtopicsForTopic = async (topicId) => {
    if (!topicId) { setSubtopics([]); return; }
    try {
      const data = await getInstutueApp(
        apiPaths.classificationConflicts + '/taxonomy/subtopics',
        { topicId },
        getHeaders()
      );
      if (data?.success) {
        setSubtopics(data.subtopics || []);
      }
    } catch (e) {
      console.error('Failed to fetch subtopics', e);
      setSubtopics([]);
    }
  };

  const handleSubjectChange = (subjectId) => {
    setSelectedSubject(subjectId);
    setSelectedTopic('');
    setSelectedSubtopic('');
    setResolutionSource('MANUAL');
    fetchTopicsForSubject(subjectId);
  };

  const handleTopicChange = (topicId) => {
    setSelectedTopic(topicId);
    setSelectedSubtopic('');
    setResolutionSource('MANUAL');
    fetchSubtopicsForTopic(topicId);
  };

  const handleUsePrimary = () => {
    setSelectedSubject(conflict.primarySubjectId || '');
    setSelectedTopic(conflict.primaryTopicId || '');
    setSelectedSubtopic(conflict.primarySubtopicId || '');
    setSelectedTags(parseTags(conflict.primaryTags));
    setResolutionSource('PRIMARY');
    if (conflict.primarySubjectId) fetchTopicsForSubject(conflict.primarySubjectId);
    if (conflict.primaryTopicId) fetchSubtopicsForTopic(conflict.primaryTopicId);
  };

  const handleUseSecondary = () => {
    setSelectedSubject(conflict.secondarySubjectId || '');
    setSelectedTopic(conflict.secondaryTopicId || '');
    setSelectedSubtopic(conflict.secondarySubtopicId || '');
    setSelectedTags(parseTags(conflict.secondaryTags));
    setResolutionSource('SECONDARY');
    if (conflict.secondarySubjectId) fetchTopicsForSubject(conflict.secondarySubjectId);
    if (conflict.secondaryTopicId) fetchSubtopicsForTopic(conflict.secondaryTopicId);
  };

  const handleKeepDB = () => {
    setSelectedSubject(conflict.dbSubjectId || '');
    setSelectedTopic(conflict.dbTopicId || '');
    setSelectedSubtopic('');
    setSelectedTags([]);
    setResolutionSource('DB');
    if (conflict.dbSubjectId) fetchTopicsForSubject(conflict.dbSubjectId);
    if (conflict.dbTopicId) fetchSubtopicsForTopic(conflict.dbTopicId);
  };

  const handleResolve = async () => {
    if (!selectedSubject) {
      toast.error('Please select a subject');
      return;
    }
    setSaving(true);
    try {
      await onResolve(conflict.id, {
        subjectId: selectedSubject,
        topicId: selectedTopic,
        subtopicId: selectedSubtopic,
        tags: selectedTags,
        notes,
        resolutionSource: resolutionSource || 'MANUAL',
      });
      onClose();
    } catch (e) {
      toast.error('Failed to resolve: ' + (e.message || e));
    } finally {
      setSaving(false);
    }
  };

  if (!conflict) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Resolve Classification Conflict — Question #{conflict.questionId}
      </DialogTitle>
      <DialogContent dividers>
        {/* Classification comparison table */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Classification Comparison</Typography>
        <ClassificationComparisonTable conflict={conflict} />

        {/* Reasoning panels (expandable) */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>AI Reasoning</Typography>
        <ReasoningPanel
          label="Primary"
          provider={conflict.primaryProvider}
          model={conflict.primaryModel}
          confidence={conflict.primaryConfidence}
          reasoning={conflict.primaryReasoning}
        />
        <ReasoningPanel
          label="Secondary"
          provider={conflict.secondaryProvider}
          model={conflict.secondaryModel}
          confidence={conflict.secondaryConfidence}
          reasoning={conflict.secondaryReasoning}
        />

        {/* Quick-fill buttons */}
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Quick Fill</Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button
            size="small"
            variant={resolutionSource === 'PRIMARY' ? 'contained' : 'outlined'}
            onClick={handleUsePrimary}
            sx={{ textTransform: 'none' }}
          >
            Use Primary
          </Button>
          <Button
            size="small"
            variant={resolutionSource === 'SECONDARY' ? 'contained' : 'outlined'}
            onClick={handleUseSecondary}
            sx={{ textTransform: 'none' }}
          >
            Use Secondary
          </Button>
          <Button
            size="small"
            variant={resolutionSource === 'DB' ? 'contained' : 'outlined'}
            onClick={handleKeepDB}
            sx={{ textTransform: 'none' }}
          >
            Keep DB
          </Button>
        </Box>

        {/* Cascading dropdowns */}
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>Select Classification</Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Subject</InputLabel>
            <Select value={selectedSubject} label="Subject" onChange={(e) => handleSubjectChange(e.target.value)}>
              <MenuItem value="">-- Select --</MenuItem>
              {subjects.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Topic</InputLabel>
            <Select value={selectedTopic} label="Topic" onChange={(e) => handleTopicChange(e.target.value)} disabled={!selectedSubject}>
              <MenuItem value="">-- Select --</MenuItem>
              {topics.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Subtopic</InputLabel>
            <Select value={selectedSubtopic} label="Subtopic" onChange={(e) => setSelectedSubtopic(e.target.value)} disabled={!selectedTopic}>
              <MenuItem value="">-- Select --</MenuItem>
              {subtopics.map((st) => (
                <MenuItem key={st.id} value={st.id}>{st.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Tags multi-select */}
        <Autocomplete
          multiple
          freeSolo
          size="small"
          options={[]}
          value={selectedTags}
          onChange={(_, newVal) => { setSelectedTags(newVal); setResolutionSource('MANUAL'); }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip label={option} size="small" {...getTagProps({ index })} />
            ))
          }
          renderInput={(params) => <TextField {...params} label="Tags" placeholder="Add tags..." />}
          sx={{ mb: 2 }}
        />

        {/* Notes */}
        <TextField
          label="Notes (optional)"
          multiline
          rows={2}
          fullWidth
          sx={{ mt: 1 }}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          helperText="Reason for this resolution — recorded in audit log."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleResolve} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Resolve'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Component ─────────────────────────────────────────────────
export default function ClassificationConflicts() {
  const canResolve = usePermission('QA Actions', 'Resolve Classification');
  const canDismiss = usePermission('QA Actions', 'Dismiss Classification');
  const [conflicts, setConflicts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [reviewedByFilter, setReviewedByFilter] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedConflict, setSelectedConflict] = useState(null);
  const pageSize = 20;

  const fetchStats = useCallback(async () => {
    try {
      const data = await getInstutueApp(apiPaths.classificationConflictsStats, null, getHeaders());
      if (data?.success) {
        setStats(data);
      }
    } catch (e) {
      console.error('Failed to fetch stats', e);
    }
  }, []);

  const fetchSubjects = useCallback(async () => {
    try {
      const data = await getInstutueApp(
        apiPaths.classificationConflicts + '/taxonomy',
        null,
        getHeaders()
      );
      if (data?.success && data.subjects) {
        setSubjects(filterBySubjectScope(data.subjects, (s) => s.id));
      }
    } catch (e) {
      console.error('Failed to fetch subjects', e);
    }
  }, []);

  const fetchConflicts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: page - 1, pageSize };
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.conflictType = typeFilter;
      if (subjectFilter) params.subjectId = subjectFilter;
      if (dateFrom) params.fromDate = dateFrom;
      if (dateTo) params.toDate = dateTo;
      if (reviewedByFilter) params.reviewedBy = reviewedByFilter;

      const data = await getInstutueApp(apiPaths.classificationConflicts, params, getHeaders());
      if (data?.success) {
        setConflicts(
          filterBySubjectScope(data.conflicts || [], (c) => c.dbSubjectId ?? c.subjectId)
        );
        setTotalPages(data.totalPages || 1);
      }
    } catch (e) {
      toast.error('Failed to load classification conflicts');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, typeFilter, subjectFilter, dateFrom, dateTo, reviewedByFilter]);

  useEffect(() => { fetchStats(); fetchSubjects(); }, [fetchStats, fetchSubjects]);
  useEffect(() => { fetchConflicts(); }, [fetchConflicts]);

  const handleResolve = async (conflictId, resolution) => {
    const data = await putInstituteAppData(
      apiPaths.classificationConflicts + '/' + conflictId + '/resolve',
      { data: resolution, headers: getHeaders() }
    );
    if (data?.success) {
      toast.success('Conflict resolved' + (data.classificationChanged ? ' — classification updated!' : ''));
      fetchConflicts();
      fetchStats();
    } else {
      throw new Error(data?.message || 'Resolution failed');
    }
  };

  const handleDismiss = async (conflictId) => {
    if (!window.confirm('Dismiss this conflict? No changes will be made.')) return;
    try {
      const data = await putInstituteAppData(
        apiPaths.classificationConflicts + '/' + conflictId + '/dismiss',
        { data: {}, headers: getHeaders() }
      );
      if (data?.success) {
        toast.info('Conflict dismissed');
        fetchConflicts();
        fetchStats();
      }
    } catch (e) {
      toast.error('Failed to dismiss conflict');
    }
  };

  const handleCorrectClassification = async (conflictId, correction) => {
    const data = await putInstituteAppData(
      apiPaths.classificationConflicts + '/' + conflictId + '/correct',
      { data: correction, headers: getHeaders() }
    );
    if (!data?.success) {
      throw new Error(data?.message || 'Failed to correct classification');
    }
  };

  const openResolveDialog = (conflict) => {
    setSelectedConflict(conflict);
    setResolveDialogOpen(true);
  };

  const openDetailDialog = (conflict) => {
    setSelectedConflict(conflict);
    setDetailDialogOpen(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Classification Conflicts
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={() => { fetchConflicts(); fetchStats(); }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <StatsDashboard stats={stats} />

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <MenuItem value="">All</MenuItem>
            {Object.entries(statusConfig).map(([key, cfg]) => (
              <MenuItem key={key} value={key}>{cfg.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Conflict Type</InputLabel>
          <Select value={typeFilter} label="Conflict Type" onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}>
            <MenuItem value="">All</MenuItem>
            {Object.entries(conflictTypeConfig).map(([key, cfg]) => (
              <MenuItem key={key} value={key}>{cfg.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Subject</InputLabel>
          <Select value={subjectFilter} label="Subject" onChange={(e) => { setSubjectFilter(e.target.value); setPage(1); }}>
            <MenuItem value="">All</MenuItem>
            {subjects.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small" type="date" label="From Date" value={dateFrom}
          onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }}
        />
        <TextField
          size="small" type="date" label="To Date" value={dateTo}
          onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true }} sx={{ minWidth: 150 }}
        />
        <TextField
          size="small" label="Reviewed By" value={reviewedByFilter}
          onChange={(e) => { setReviewedByFilter(e.target.value); setPage(1); }}
          placeholder="Email or name" sx={{ minWidth: 160 }}
        />
        {(dateFrom || dateTo || reviewedByFilter) && (
          <Chip
            label="Clear date/user filters"
            onDelete={() => { setDateFrom(''); setDateTo(''); setReviewedByFilter(''); setPage(1); }}
            size="small" color="primary" variant="outlined"
          />
        )}
      </Box>

      {/* Conflicts Table */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : conflicts.length === 0 ? (
        <Alert severity="info">No classification conflicts found matching your filters.</Alert>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Question ID</TableCell>
                  <TableCell>DB Subject</TableCell>
                  <TableCell>DB Topic</TableCell>
                  <TableCell>Primary</TableCell>
                  <TableCell>Secondary</TableCell>
                  <TableCell>Conflict Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {conflicts.map((c) => {
                  const typeCfg = conflictTypeConfig[c.conflictType] || {};
                  const statusCfg = statusConfig[c.status] || {};

                  return (
                    <TableRow
                      key={c.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => (c.status === 'PENDING' && canResolve) ? openResolveDialog(c) : openDetailDialog(c)}
                    >
                      <TableCell><strong>#{c.questionId}</strong></TableCell>
                      <TableCell>{c.dbSubjectName || '—'}</TableCell>
                      <TableCell>{c.dbTopicName || '—'}</TableCell>
                      {/* Primary stacked */}
                      <TableCell>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <span style={{
                              color: c.primarySubjectName === c.dbSubjectName ? '#22c55e' : '#dc3545',
                              fontWeight: 'bold',
                              fontSize: 12,
                            }}>
                              {c.primarySubjectName || '—'}
                            </span>
                            {c.primaryProvider && (
                              <Chip label={c.primaryProvider} size="small" sx={{
                                fontSize: 9, height: 16,
                                backgroundColor: providerColor[c.primaryProvider] || '#666', color: '#fff'
                              }} />
                            )}
                          </Box>
                          <Typography variant="caption" sx={{
                            color: c.primaryTopicName === c.dbTopicName ? '#22c55e' : '#dc3545',
                            display: 'block', fontSize: 11
                          }}>
                            {c.primaryTopicName || '—'}
                          </Typography>
                          {c.primarySubtopicName && (
                            <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: 10 }}>
                              {c.primarySubtopicName}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      {/* Secondary stacked */}
                      <TableCell>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <span style={{
                              color: c.secondarySubjectName === c.dbSubjectName ? '#22c55e' : '#dc3545',
                              fontWeight: 'bold',
                              fontSize: 12,
                            }}>
                              {c.secondarySubjectName || '—'}
                            </span>
                            {c.secondaryProvider && (
                              <Chip label={c.secondaryProvider} size="small" sx={{
                                fontSize: 9, height: 16,
                                backgroundColor: providerColor[c.secondaryProvider] || '#666', color: '#fff'
                              }} />
                            )}
                          </Box>
                          <Typography variant="caption" sx={{
                            color: c.secondaryTopicName === c.dbTopicName ? '#22c55e' : '#dc3545',
                            display: 'block', fontSize: 11
                          }}>
                            {c.secondaryTopicName || '—'}
                          </Typography>
                          {c.secondarySubtopicName && (
                            <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: 10 }}>
                              {c.secondarySubtopicName}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={typeCfg.icon}
                          label={typeCfg.label || c.conflictType}
                          size="small"
                          sx={{ backgroundColor: typeCfg.color, color: '#fff', fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusCfg.label || c.status}
                          size="small"
                          variant="outlined"
                          sx={{ borderColor: statusCfg.color, color: statusCfg.color }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '—'}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                          {c.status === 'PENDING' && (
                            <>
                              {canResolve && (
                                <Tooltip title="Resolve">
                                  <IconButton size="small" color="primary" onClick={() => openResolveDialog(c)}>
                                    <ThumbUpIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canDismiss && (
                                <Tooltip title="Dismiss">
                                  <IconButton size="small" color="default" onClick={() => handleDismiss(c.id)}>
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                          {c.status !== 'PENDING' && (
                            <Tooltip title="View details">
                              <IconButton size="small" onClick={() => openDetailDialog(c)}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" />
          </Box>
        </>
      )}

      <ResolveDialog
        open={resolveDialogOpen}
        conflict={selectedConflict}
        onClose={() => setResolveDialogOpen(false)}
        onResolve={handleResolve}
      />

      <DetailDialog
        open={detailDialogOpen}
        conflict={selectedConflict}
        onClose={() => setDetailDialogOpen(false)}
        onCorrectClassification={handleCorrectClassification}
        onRefresh={() => { fetchConflicts(); fetchStats(); }}
      />
    </Box>
  );
}
